import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { pickFormationText } from "./karateFormationLocale.js";
import { formationCompleteStep } from "@/api/karateR0FormationApi.js";
import { getFormationOpenScreenLabel } from "./karateFormationUi.js";
import { openFormationPracticeTab } from "./formationPracticeTab.js";

/**
 * @param {object} props
 * @param {object} props.track
 * @param {object} props.progress
 * @param {string} props.stepId
 * @param {'fr'|'en'} props.locale
 * @param {object} props.ui
 * @param {(next: object) => void} props.onProgress
 * @param {() => void} props.onQuit
 */
export function KarateFormationRunner({ track, progress, stepId, locale, ui, onProgress, onQuit }) {
  const navigate = useNavigate();
  const steps = track?.steps ?? [];
  const stepIndex = steps.findIndex((s) => s.id === stepId);
  const step = steps[stepIndex] ?? steps[0];
  const [busy, setBusy] = useState(false);
  const [navValidated, setNavValidated] = useState(false);
  const [screenChecked, setScreenChecked] = useState(false);

  useEffect(() => {
    setNavValidated(false);
    setScreenChecked(false);
  }, [step?.id]);

  const goStep = useCallback(
    (targetId) => {
      const params = new URLSearchParams({ track: track.id, step: targetId });
      navigate(`/formation?${params.toString()}`, { replace: true });
    },
    [navigate, track.id],
  );

  async function handleComplete() {
    if (!step) return;
    setBusy(true);
    try {
      const { response, body } = await formationCompleteStep(track.id, step.id);
      if (response.ok) {
        onProgress(body.progress);
        const next = body.progress.activeStepId;
        if (next && next !== step.id) goStep(next);
      }
    } finally {
      setBusy(false);
    }
  }

  function handleOpenRoute(routePath = step?.route) {
    if (!routePath) return;
    if (step?.validation === "navigate") {
      setNavValidated(true);
    }
    openFormationPracticeTab(routePath);
  }

  const prevStep = stepIndex > 0 ? steps[stepIndex - 1] : null;
  const nextStep = stepIndex < steps.length - 1 ? steps[stepIndex + 1] : null;
  const isDone = (progress?.completedStepIds ?? []).includes(step?.id);
  const hasOpenTarget = Boolean(step?.route) || Boolean(step?.guidedFlow?.length);
  const openLabel = getFormationOpenScreenLabel(step?.route, locale);

  if (!step) return null;

  return (
    <section className="karate-formation-runner" data-testid={`formation-runner-${track.id}`}>
      <header className="karate-formation-runner__head">
        <button type="button" className="btn-primary-outline" data-testid="formation-quit" onClick={onQuit}>
          {ui.quitTraining}
        </button>
        <p className="muted">
          {stepIndex + 1} / {steps.length}
        </p>
        <h2>{pickFormationText(step.title, locale)}</h2>
        <p>{pickFormationText(step.objective, locale)}</p>
        {step.actionSafety === "demo" || step.actionSafety === "observe_only" ? (
          <p className="karate-formation-runner__safe">
            <DemoBadge /> {ui.demoSafe}
          </p>
        ) : null}
      </header>

      <div className="karate-formation-runner__vcf">
        {step.see ? (
          <article className="karate-formation-vcf-card">
            <h3>{ui.see}</h3>
            <p>{pickFormationText(step.see, locale)}</p>
            {hasOpenTarget ? (
              <p className="karate-formation-newtab-hint" data-testid="formation-newtab-hint">
                <span className="karate-formation-newtab-hint__desktop">{ui.newTabHint}</span>
                <span className="karate-formation-newtab-hint__mobile">{ui.newTabHintMobile}</span>
              </p>
            ) : null}
          </article>
        ) : null}
        <article className="karate-formation-vcf-card">
          <h3>{ui.understand}</h3>
          <p>{pickFormationText(step.objective, locale)}</p>
        </article>
        <article className="karate-formation-vcf-card">
          <h3>{ui.do}</h3>
          <p>{pickFormationText(step.do ?? step.objective, locale)}</p>
          {step.route ? (
            <>
              {!step.see && hasOpenTarget ? (
                <p className="karate-formation-newtab-hint" data-testid="formation-newtab-hint">
                  <span className="karate-formation-newtab-hint__desktop">{ui.newTabHint}</span>
                  <span className="karate-formation-newtab-hint__mobile">{ui.newTabHintMobile}</span>
                </p>
              ) : null}
              <button type="button" className="btn-primary" data-testid="formation-open-route" onClick={() => handleOpenRoute(step.route)}>
                {openLabel}
              </button>
            </>
          ) : null}
          {step.guidedFlow?.length ? (
            <ol className="karate-formation-guided-flow" data-testid="formation-guided-flow">
              {step.guidedFlow.map((route, idx) => (
                <li key={`${step.id}-${idx}-${route}`}>
                  <button
                    type="button"
                    className="btn-primary-outline btn-sm karate-formation-guided-open"
                    data-testid={`formation-guided-open-${idx}`}
                    onClick={() => handleOpenRoute(route)}
                  >
                    {getFormationOpenScreenLabel(route, locale)}
                  </button>
                </li>
              ))}
            </ol>
          ) : null}
          {step.route && !isDone ? (
            <button
              type="button"
              className="btn-primary-outline btn-sm karate-formation-verified-screen"
              data-testid="formation-verified-screen"
              onClick={() => setScreenChecked(true)}
            >
              {ui.verifiedScreen}
            </button>
          ) : null}
          {screenChecked ? (
            <p className="karate-formation-runner__validated" data-testid="formation-screen-checked">
              ✓ {locale === "en" ? "Ready to mark this step complete." : "Vous pouvez marquer l'étape terminée."}
            </p>
          ) : null}
          {step.validation === "navigate" && navValidated ? (
            <p className="karate-formation-runner__validated" data-testid="formation-nav-validated">
              ✓{" "}
              {locale === "en"
                ? "Screen opened — you can mark this step complete."
                : "Écran ouvert — vous pouvez marquer l'étape terminée."}
            </p>
          ) : null}
        </article>
      </div>

      <footer className="karate-formation-runner__foot">
        {prevStep ? (
          <button type="button" className="btn-primary-outline" onClick={() => goStep(prevStep.id)}>
            {ui.previous}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="btn-primary"
          data-testid="formation-complete-step"
          disabled={busy || isDone}
          onClick={handleComplete}
        >
          {isDone ? (locale === "en" ? "Completed" : "Terminé") : ui.markComplete}
        </button>
        {nextStep ? (
          <button type="button" className="btn-primary-outline" onClick={() => goStep(nextStep.id)}>
            {ui.next}
          </button>
        ) : (
          <span />
        )}
      </footer>
    </section>
  );
}
